import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ElementType, FC, ReactElement, ReactNode } from 'react';

import { IGraphicSize } from '@gem/models/commonModel';
import { IconView, IconViewSizes } from '@gem/ui-kit';

import { LayoutImagePreview } from './LayoutImagePreview/LayoutImagePreview';
import { useLayoutPresenter } from './MiniatureMessageLayout.presenter';
import styles from './MiniatureMessageLayout.styles.m.css';

export interface ILayoutProps {
  className?: string;
  titleClassName?: string;
  textClassName?: string;
  iconClassName?: string;
  imageUrl?: string;
  graphicSize?: IGraphicSize;
  Icon?: ElementType;
  title: string | ReactElement;
  text?: string;
  messageId: string;
  isVideo?: boolean;
  link?: string;
  onClick?(...args: any[]): void;
  children?: ReactNode | ReactNode[];
  preventInteraction?: boolean;
  preventFocusOnClick?: boolean;
}

export const MiniatureMessageLayout: FC<ILayoutProps> = observer((props) => {
  const {
    className,
    titleClassName,
    textClassName,
    iconClassName,
    imageUrl,
    graphicSize,
    Icon,
    title,
    text,
    messageId,
    isVideo = false,
    children,
    link,
    onClick,
    preventInteraction,
    preventFocusOnClick,
  } = props;

  const presenter = useLayoutPresenter({
    messageId,
    imageUrl,
    onClick,
    preventInteraction,
    preventFocusOnClick,
  });

  const containerClassName = classNames(styles.container, className, {
    [styles.preventInteraction]: preventInteraction,
  });

  return (
    <div className={containerClassName}>
      {link && !preventInteraction ? (
        <a
          className={classNames(styles.focus, styles.link)}
          href={link}
          target="_blank"
          rel="noreferrer"
        >
          {link}
        </a>
      ) : (
        <button
          aria-label="scroll to message"
          className={styles.focus}
          onClick={presenter.focusMessage}
        />
      )}
      {presenter.hasImage && (
        <LayoutImagePreview
          isVideo={isVideo}
          imageUrl={presenter.imageUrl || ''}
          graphicSize={graphicSize}
        />
      )}
      <div className={styles.content}>
        <span className={classNames(styles.title, titleClassName)}>{title}</span>
        <div className={classNames(styles.subtitle, textClassName)}>
          {Icon && (
            <IconView
              Icon={Icon}
              size={IconViewSizes.Small}
              className={classNames(styles.icon, iconClassName)}
            />
          )}
          {children ? children : text}
        </div>
      </div>
    </div>
  );
});
